import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { clearAuthData, selectUserInformations } from "../../../features/authSlice";
import ProfileIcon from '../../../assets/svg/ProfileIcon.svg';
import Settings from '../../../assets/svg/Settings.svg';
import Logout from '../../../assets/svg/Logout.svg';
import { Logout as LogoutRequest } from "../../../services/axiosServices";
import Loader from "../../Live/Loader";
import { showToast } from "../../../utils/utils";
import { useNavigate } from "react-router-dom";
import { persistor } from "../../../redux/store";
import { clearAuthCookies } from "../../../utils/cookieUtils";

const dropdownVariants = {
    hidden: { opacity: 0, y: -20, scale: 0.95 },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            type: "spring",
            stiffness: 300,
            damping: 20,
            staggerChildren: 0.1
        }
    },
    exit: {
        opacity: 0,
        y: 20,
        scale: 0.95,
        transition: { duration: 0.2 }
    }
};

const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
    hover: { scale: 1.02 },
    tap: { scale: 0.98 }
};

const Dropdown = ({ setAvatarDropdown }) => {
    const [loader, setLoader] = useState(false);
    const dispatch = useDispatch();
    const user = useSelector(selectUserInformations);
    const navigate = useNavigate();

    const handleLogout = async () => {
        setLoader(true);
        try {
            const response = await LogoutRequest(user);
            if (response.status === 200 && response.errorCode === 0) {
                dispatch(clearAuthData());

                try {
                    clearAuthCookies();
                } catch (cookieError) {
                    console.error('Failed to clear cookies:', cookieError);
                }
                persistor.purge();
                showToast("Bye Bye", response.message || "Logged out successfully.", "success");
                navigate('/')
            } else {
                showToast("Oops, Something went wrong", response.message || "Logout failed.", "error");
            }
        } catch (error) {
            console.error("Logout error:", error);
            showToast("Oops, Something went wrong", "Please try again.", "error");
        } finally {
            setLoader(false);
        }
    };

    return (
        <AnimatePresence>
            <motion.div
                className="relative"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={dropdownVariants}
            >
                {/* Animated Triangle */}
                <motion.div
                    className="absolute top-[0.7rem] right-[1.2rem] xs:right-[0.7rem] sm:right-[1rem]
              w-0 h-0 border-l-[0.75rem] border-l-transparent border-r-[0.75rem] border-r-transparent 
              border-b-[0.75rem] border-b-white md:top-[1.2rem] xl:top-[1.3rem]"
                />

                {/* Main Container */}
                <motion.div
                    className="absolute flex flex-col w-[92vw] max-w-[28rem] xs:max-w-[30rem] 
              sm:max-w-[32rem] md:max-w-[34rem] xl:max-w-[36rem] 3xl:max-w-[38rem]
              h-[70vh] min-h-[32rem] max-h-[50rem] md:h-[65vh] top-[2rem] right-[0.5rem] 
              bg-white rounded-lg shadow-xl z-50 backdrop-blur-[0.1rem]"
                >
                    {/* Profile Section */}
                    <motion.div
                        className="relative flex flex-col w-full min-h-[7.5rem] bg-[#DADADA]/80 
                mt-[12vh]"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                    >
                        <motion.div
                            className="relative -top-[1rem] ll:top-0 flex flex-col md:flex-row md:items-center 
                  md:pl-[2rem] xl:pl-[2.5rem]"
                            variants={itemVariants}
                        >
                            <motion.img
                                src={user.image_url}
                                alt="Profile Picture"
                                className="w-[7.5rem] h-[7.5rem] xs:w-[8rem] xs:h-[8rem] md:w-[10rem] md:h-[10rem] ll:h-[13.3rem] ll:w-[13.3rem]
                     rounded-full border-[0.725rem] xl:w-[11rem] xl:h-[11rem] border-white/90 
                    mx-auto md:mx-0 shadow-md hover:shadow-lg transition-shadow"
                                whileHover={{ scale: 1.05 }}
                                transition={{ type: "spring", stiffness: 300 }}
                            />
                            <motion.div
                                className="flex flex-col justify-center text-center md:text-left mt-[1.5rem] 
                    md:mt-0 md:ml-[2rem] xl:ml-[2.5rem] w-full"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: 0.2 }}
                            >
                                <h2 className="text-[1.6rem] xs:text-[1.8rem] md:text-[2rem] xl:text-[2.2rem] 
                    font-medium text-[#134F98] break-words leading-tight tracking-tight">
                                    {user.name}
                                </h2>
                                <p className="text-[#858585] font-medium text-[0.9rem] md:text-[1rem] 
                    xl:text-[1.1rem] mt-[0.4rem]">
                                    {user.email}
                                </p>
                            </motion.div>
                        </motion.div>
                    </motion.div>

                    {/* Buttons Section */}
                    <motion.div
                        className="relative flex flex-col justify-center items-center p-[1.2rem] 
                md:p-[1.5rem] space-y-[1.2rem] overflow-y-auto"
                        variants={dropdownVariants}
                    >
                        {[
                            { icon: ProfileIcon, text: "Profile", action: () => navigate('/dashboard/profile') },
                            { icon: Settings, text: "Settings", action: () => { } },
                            { icon: Logout, text: "Logout", action: handleLogout }
                        ].map((item, index) => (
                            <motion.button
                                key={item.text}
                                variants={itemVariants}
                                whileHover="hover"
                                whileTap="tap"
                                onClick={() => {
                                    setAvatarDropdown(false);
                                    item.action();
                                }}
                                className="w-full max-w-[90vw] md:max-w-[28rem] h-[4.5rem] md:h-[5rem] 
                    text-[#858585] text-[1.1rem] md:text-[1.2rem] font-normal bg-white/95 
                    border border-[#DADADA]/50 rounded-xl hover:bg-[#134F98] hover:text-white/95 
                    transition-all duration-200 flex items-center justify-center relative px-[1.5rem]
                    shadow-sm hover:shadow-md backdrop-blur-[0.2rem]"
                            >
                                <img
                                    className="w-[1.5rem] h-[1.5rem] md:w-[1.7rem] md:h-[1.7rem] 
                      absolute left-[1.5rem]"
                                    src={item.icon}
                                    alt={item.text}
                                />
                                <span className="translate-x-[0.8rem]">{item.text}</span>
                                {item.text === "Logout" && loader && (
                                    <motion.div
                                        className="absolute right-[1.5rem]"
                                        initial={{ scale: 0 }}
                                        animate={{ scale: 1 }}
                                    >
                                        <Loader />
                                    </motion.div>
                                )}
                            </motion.button>
                        ))}
                    </motion.div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default Dropdown;